import { ReactNode, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useIsFirstRender from '../../hooks/useIsFirstRender';
import { FilterPreferenceRecognizedLists } from '../../services/modules/userContextPreferences/userContextPreferences.types';
import useSavableHideableFilter from '../Crud/filters/SavableHideableFilter/hooks/useSavableHideableFilter';
import useCommonFilterLogic from '../Crud/filters/sharedBetweenFilters/hooks/useCommonFilterLogic';
import { FilterOptionV3, FilterProps } from '../Crud/filters/sharedBetweenFilters/types';
import useWidth from '../Hooks/useWidth';
import FilterPopover from './components/FilterPopover/FilterPopover';
import MobileDrawer from './components/MobileDrawer/MobileDrawer';
import MobileFilterBar from './components/MobileFilterBar';
import UnifiedFilterBar from './components/UnifiedFilterBar';
import { useFilterV3Context } from './contexts/FilterV3.context';
import useFilterV3Styles from './filter.styles';

export interface FilterV3Props {
  options: FilterOptionV3[];
  FixedFilters?: ReactNode[];
  list: FilterPreferenceRecognizedLists;
  searchField?: ReactNode;
  isSearchActive?: boolean;
  onClearSearch?: () => void;
  unifiedBar?: boolean;
}

const FilterV3 = ({
  options,
  FixedFilters,
  list,
  searchField,
  isSearchActive,
  onClearSearch,
  unifiedBar,
}: FilterV3Props) => {
  const { mobileWidth } = useWidth();
  const useDesktopLayout = unifiedBar || !mobileWidth;
  const { classes } = useFilterV3Styles();

  const history = useHistory();
  const isFirstRender = useIsFirstRender();

  const { clearAllFilters } = useCommonFilterLogic({ filters: options });

  const handleClearAll = () => {
    clearAllFilters();
    onClearSearch?.();
  };
  const { saveFilters, canSaveFilters } = useSavableHideableFilter({
    filters: options as unknown as FilterProps<true>[],
    list,
  });

  const {
    anchorEl,
    handleClickSelectedFilter,
    handleClosePopover,
    menu,
    handleSelectFilter,
    onChangeAnchorEl,
    formattedFilters,
    handleBackToFirstStep,
  } = useFilterV3Context();

  const hasSelectedFilter = formattedFilters.some((filter) => filter.hasValue && !filter.fixed);
  const hasFixedFilter = formattedFilters.some((filter) => filter.fixed);
  const hasActiveFixedFilter = formattedFilters.some((filter) => filter.fixed && filter.hasValue);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!isFirstRender && canSaveFilters) {
      saveFilters();
    }
  }, [history.location.search]);

  return (
    <>
      {useDesktopLayout ? (
        <FilterPopover
          onClickBack={handleBackToFirstStep}
          anchorEl={anchorEl}
          handleClosePopover={handleClosePopover}
          classes={classes}
          menu={menu}
          options={options}
          handleSelectFilter={handleSelectFilter}
          list={list}
        />
      ) : (
        <MobileDrawer
          anchorEl={anchorEl}
          handleClosePopover={handleClosePopover}
          classes={classes}
          menu={menu}
          options={options}
          handleSelectFilter={handleSelectFilter}
          list={list}
        />
      )}
      {useDesktopLayout ? (
        <UnifiedFilterBar
          searchField={searchField}
          fixedFilters={FixedFilters}
          list={list}
          formattedFilters={formattedFilters}
          hasSelectedFilter={hasSelectedFilter}
          hasFixedFilter={hasFixedFilter}
          hasActiveFixedFilter={hasActiveFixedFilter}
          isSearchActive={isSearchActive}
          onClickSelectedFilter={handleClickSelectedFilter}
          onClearAll={handleClearAll}
        />
      ) : (
        <MobileFilterBar
          fixedFilters={FixedFilters}
          hasSelectedFilter={hasSelectedFilter}
          hasFilterOptions={options.length > 0}
          list={list}
          formattedFilters={formattedFilters}
          onClickSelectedFilter={handleClickSelectedFilter}
          onClickAddFilter={(event) => onChangeAnchorEl(event.currentTarget)}
        />
      )}
    </>
  );
};

export default FilterV3;
